import { useMemo } from 'react';
import {
  groupTurns,
  markResolvedConfirmations,
  type AssistantVisualGroup,
  type VisualGroup,
} from './groupTurns';
import { ChatUserBubble } from './ChatUserBubble';
import { ChatAssistantBubble } from './ChatAssistantBubble';
import { isConfirmationUserMessage } from './confirmationMessages';
import type { ChatTurnResponse } from '../../types';

export interface ChatTimelineProps {
  turns: ChatTurnResponse[];
  /** Visual groups produced by streams completed since the last replay
   *  fetch — appended after the BE-replayed groups. */
  localGroups?: VisualGroup[];
  /** User text just sent, rendered optimistically while the stream runs. */
  pendingUserText?: string | null;
  /** Live-growing assistant turn from SSE events. */
  inFlight?: AssistantVisualGroup | null;
  /** True while the SSE stream is actively running. Drives the typing
   *  indicator. When false (e.g. stream errored), the in-flight bubble is
   *  treated as a partial-progress snapshot — and an empty one is dropped
   *  entirely so a truncated-before-first-delta turn doesn't render as a
   *  blank grey card. SC-2153. */
  isStreaming?: boolean;
}

export function ChatTimeline({
  turns,
  localGroups = [],
  pendingUserText,
  inFlight,
  isStreaming = true,
}: ChatTimelineProps) {
  const replayGroups = useMemo(() => groupTurns(turns), [turns]);

  const merged: VisualGroup[] = [...replayGroups, ...localGroups];
  if (pendingUserText) {
    merged.push({
      kind: 'user',
      turnId: '__pending_user__',
      blocks: [{ type: 'text', text: pendingUserText }],
    });
  }
  if (inFlight && (isStreaming || inFlight.items.length > 0)) {
    merged.push(inFlight);
  }

  // Cross-source confirmation resolution. The second `apply_finding` tool_use
  // (with confirmed=true) typically lands in `localGroups` or `inFlight` while
  // the original confirmation card is still in `replayGroups` — without this
  // pass, the Confirm/Cancel footer keeps showing after the finding's been
  // applied.
  const all = markResolvedConfirmations(merged);

  return (
    <div data-testid="chat-timeline">
      {all.map((group) => {
        if (group.kind === 'user') {
          const text = group.blocks.map((b) => b.text).join('\n\n');
          // SC-2157: confirm/cancel messages are sent to the agent (BE needs
          // the finding_id for disambiguation) but suppressed in the UI —
          // the confirmation card itself shows the user's decision, so a
          // separate "Yes, apply this finding." bubble is redundant.
          if (isConfirmationUserMessage(text)) return null;
          return <ChatUserBubble key={group.turnId} text={text} />;
        }
        const isLive = group === inFlight && isStreaming;
        return (
          <ChatAssistantBubble
            key={group.turnIds.join('-') || '__inflight__'}
            items={group.items}
            isLive={isLive}
          />
        );
      })}
    </div>
  );
}
